import { Presentation, PlayCircle, FileText, ExternalLink, ArrowUpRight, Copy, Check, BookOpen, Video, Package } from "lucide-react";
import { useState } from "react";
import { usePrimaryColor } from "../../components/PrimaryColorContext";


/**
 * ResourcePreviewGrid - Premium resource preview grid with embedded previews
 * for Canva presentations, YouTube videos, and Google Drive links.
 * @param {object} props
 * @param {Array} props.resources - Array of resource objects { _id, name, link, fileId?, type? }
 * @param {string} props.label - Section label
 * @param {"canva"|"youtube"|"link"|"auto"} props.previewType - Type of preview to render
 * @param {React.ReactNode} [props.icon] - Optional icon for section header
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 2.0.0
 * @since 2026-04-14
 */
export default function ResourcePreviewGrid({ resources, label, previewType, icon }) {
  if (!resources || resources.length === 0) return null;

  const cols =
    previewType === "youtube" || previewType === "canva" || previewType === "auto"
      ? "grid-cols-1 lg:grid-cols-2"
      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
        {icon}
        {label}
      </h4>
      <div className={`grid ${cols} gap-4`}>
        {resources.map((r, i) => (
          <ResourceCard
            key={r._id || r.link || i}
            resource={r}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * ResourceCard - Auto-detects embeddable URLs and renders iframe or link card
 * @param {object} props
 * @param {object} props.resource - Resource object { _id, name, link, fileId? }
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 2.0.0
 * @since 2026-04-14
 */
export function ResourceCard({ resource }) {
  const { primaryColor } = usePrimaryColor();
  if (!resource) return null;
  const url = resource.fileId
    ? `https://docs.google.com/document/d/${resource.fileId}`
    : resource.link || "";

  const embedUrl = getEmbedUrl(url);

  if (embedUrl) {
    return (
      <div className="w-full rounded-[10px] overflow-hidden border border-slate-200/80 bg-slate-50/50 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div style={{ aspectRatio: "16/9" }}>
          <iframe
            src={embedUrl}
            className="w-full h-full border-0"
            allowFullScreen
            loading="lazy"
            title={resource.name}
          />
        </div>
        <div className="px-3 py-2 flex items-center justify-between border-t border-slate-100">
          <div className="text-sm text-slate-700 truncate flex-1 mr-2" title={resource.name}>
            {resource.name}
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 text-xs font-medium text-${primaryColor}-800 hover:underline flex-shrink-0`}
          >
            <ExternalLink size={12} />
            Open
          </a>
        </div>
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-3 p-3 bg-slate-50 hover:bg-${primaryColor}-50 border border-slate-200 hover:border-${primaryColor}-800/20 rounded-[10px] transition-all duration-150 group cursor-pointer`}
    >
      <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
        <FileText size={18} className={`text-${primaryColor}-800`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-800 text-sm truncate">{resource.name || "Untitled Resource"}</div>
        <div className="text-xs text-slate-400 truncate">Open link</div>
      </div>
      <ExternalLink size={14} className={`text-slate-400 group-hover:text-${primaryColor}-800 flex-shrink-0`} />
    </a>
  );
}

/**
 * CopyButton - Small copy button with feedback
 * @param {object} props
 * @param {string|string[]} props.data - Data to copy
 * @param {string} props.section - Section identifier for copy state
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 1.0.0
 * @since 2026-04-14
 */
export function CopyButton({ data }) {
  const [copied, setCopied] = useState(false);
  const { primaryColor } = usePrimaryColor();

  const handleCopy = () => {
    if (!data || (Array.isArray(data) && data.length === 0)) return;
    const text = Array.isArray(data) ? data.join(`, `) : data;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-${primaryColor}-800 hover:bg-${primaryColor}-50 rounded-[10px] transition-all duration-150 cursor-pointer`}
      title="Copy to clipboard"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

/**
 * getEmbedUrl - Detects YouTube, Canva, Google Slides URLs and returns embed URL
 * @param {string} url
 * @returns {string|null}
 * @author opencode (glm-5.1) + Forge
 * @version 2.0.0
 * @since 2026-04-14
 */
export function getEmbedUrl(url) {
  if (!url) return null;
  try {
    const ytMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i);
    if (ytMatch && ytMatch[1]) {
      return `https://www.youtube.com/embed/${ytMatch[1]}`;
    }

    if (url.includes("docs.google.com")) {
      if (url.includes("/presentation/d/")) {
        return url.replace(/\/edit.*$/, "/embed?start=false&loop=false&delayms=3000");
      }
    }

    if (url.includes("canva.com")) {
      const cleanUrl = url.split("?")[0].split("#")[0];
      if (cleanUrl.match(/\/view$/)) {
        return `${cleanUrl}?embed`;
      }
      if (cleanUrl.match(/\/watch$/)) {
        return `${cleanUrl}?embed`;
      }
      if (cleanUrl.match(/\/edit$/)) {
        return cleanUrl.replace(/\/edit$/, "/view?embed");
      }
      if (!cleanUrl.match(/\/(view|edit|watch)/)) {
        return `${cleanUrl.replace(/\/$/, "")}/view?embed`;
      }
    }

    return null;
  } catch {
    return null;
  }
}

export const iconMap = {
  canva: <Presentation size={14} />,
  youtube: <Video size={14} />,
  link: <FileText size={14} />,
  modelLessonPlan: <BookOpen size={14} />,
  otherLessonPlans: <BookOpen size={14} />,
  resources: <Package size={14} />,
};
